import React, { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ScatterChart, Scatter, ZAxis, PieChart, Pie, Cell, ReferenceLine
} from 'recharts';
import ChartCard from '../../components/ui/ChartCard';
import { formatLargeNumber } from '../../components/ui/chartUtils';

interface Snapshot {
  timestamp: string;
  value: number;
}

interface ReportTabProps {
  data: {
    views: Snapshot[];
    likes: Snapshot[];
    comments: Snapshot[];
    shares: Snapshot[];
    engagement: Snapshot[];
  };
  video: any;
  account?: {
    username: string;
    name: string;
    avatar_url?: string;
  } | null;
  username?: string;
}

// Colors from legacy template
const COLORS = {
  blue: '#1f6bff',
  orange: '#fb923c',
  red: '#ff1764',
  cyan: '#22d3ee',
  green: '#2ba56a',
  grey: '#94a3b8',
  purple: '#8b5cf6'
};

// Top quadrant labels — placed ABOVE the chart
const QuadrantLabelsTop: React.FC<{ labels: [string, string] }> = ({ labels }) => (
  <div className="grid grid-cols-2 gap-x-4 mb-2 px-2">
    <div className="text-[10px] text-sky-400/70 text-left truncate" title={labels[0]}>{'\u2196\uFE0E'} {labels[0]}</div>
    <div className="text-[10px] text-emerald-400/70 text-right truncate" title={labels[1]}>{'\u2197\uFE0E'} {labels[1]}</div>
  </div>
);

// Bottom quadrant labels — placed BELOW the chart
const QuadrantLabelsBottom: React.FC<{ labels: [string, string] }> = ({ labels }) => (
  <div className="grid grid-cols-2 gap-x-4 mt-2 px-2">
    <div className="text-[10px] text-amber-400/70 text-left truncate" title={labels[0]}>{'\u2199\uFE0E'} {labels[0]}</div>
    <div className="text-[10px] text-rose-400/70 text-right truncate" title={labels[1]}>{'\u2198\uFE0E'} {labels[1]}</div>
  </div>
);

const ReportTab: React.FC<ReportTabProps> = ({ data }) => {

  // Transform data into a unified array for Recharts
  const unifiedData = useMemo(() => {
    if (!data.views.length) return [];

    return data.views.map((view, i) => {
      const likes = data.likes[i]?.value || 0;
      const comments = data.comments[i]?.value || 0;
      let shares = data.shares[i]?.value;
      if (shares === undefined || shares === null) {
        const estimatedInteractions = (data.engagement[i]?.value || 0) * view.value;
        shares = Math.max(0, estimatedInteractions - likes - comments);
      }

      const viewCount = Math.max(1, view.value);
      // engagement value from API is already a percentage (e.g. 3.4 = 3.4%)
      const engagementPct = data.engagement[i]?.value || 0;

      const shareRate = (shares / viewCount) * 100;
      const commentRate = (comments / viewCount) * 100;
      const likeRate = (likes / viewCount) * 100;

      const CLR = likes > 0 ? comments / likes : 0;
      const SLR = likes > 0 ? shares / likes : 0;

      return {
        timestamp: new Date(view.timestamp).getTime(),
        dateStr: new Date(view.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
        views: viewCount,
        likes,
        comments,
        shares,
        engagement: engagementPct,
        shareRate,
        commentRate,
        likeRate,
        CLR,
        SLR,
        lowConfidence: view.value < 1000
      };
    });
  }, [data]);

  const latest = unifiedData[unifiedData.length - 1];

  const mixData = useMemo(() => {
    if (!latest) return [];
    return [
      { name: 'Likes', value: latest.likes, color: COLORS.blue },
      { name: 'Comments', value: latest.comments, color: COLORS.red },
      { name: 'Shares', value: latest.shares, color: COLORS.orange }
    ];
  }, [latest]);

  // Compute explicit domains + visual center for each scatter axis
  // By controlling the domain, the crosshair is guaranteed to be at the visual center
  const chartDomains = useMemo(() => {
    if (!unifiedData.length) return null;
    const linearDomain = (key: keyof typeof unifiedData[0]) => {
      const vals = unifiedData.map(d => d[key] as number).filter(v => isFinite(v));
      if (!vals.length) return { min: 0, max: 1, mid: 0.5 };
      const mn = Math.min(...vals);
      const mx = Math.max(...vals);
      const span = mx - mn || Math.abs(mx) * 0.1 || 1;
      const pad = span * 0.15;
      const dMin = Math.max(0, mn - pad);
      const dMax = mx + pad;
      return { min: dMin, max: dMax, mid: (dMin + dMax) / 2 };
    };
    const logDomain = (key: keyof typeof unifiedData[0]) => {
      const vals = unifiedData.map(d => d[key] as number).filter(v => isFinite(v) && v > 0);
      if (!vals.length) return { min: 1, max: 10, mid: Math.sqrt(10) };
      const mn = Math.min(...vals);
      const mx = Math.max(...vals);
      const pad = 1.3;
      const dMin = mn / pad;
      const dMax = mx * pad;
      return { min: dMin, max: dMax, mid: Math.sqrt(dMin * dMax) };
    };
    return {
      views: logDomain('views'),
      shareRate: linearDomain('shareRate'),
      commentRate: linearDomain('commentRate'),
      CLR: linearDomain('CLR'),
      SLR: linearDomain('SLR'),
      engagement: linearDomain('engagement'),
    };
  }, [unifiedData]);

  if (!unifiedData.length || !chartDomains) {
    return <div className="text-center text-slate-500 py-10">No data available for report.</div>;
  }

  // Common Tooltip
  const CustomTooltip = ({ active, payload, label, formatter }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 border border-slate-700 p-3 rounded shadow-xl text-xs">
          {label && <div className="mb-2 font-bold text-slate-300">
            {typeof label === 'number' ? new Date(label).toLocaleString() : label}
          </div>}
          {payload.map((entry: any, i: number) => (
            <div key={i} className="flex items-center gap-2 mb-1" style={{ color: entry.color }}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }}></span>
              <span className="capitalize">{entry.name}:</span>
              <span className="font-mono font-bold">
                {formatter ? formatter(entry.value, entry.name, entry) :
                  (typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* CARD 2: Time-Series Overview */}
        <ChartCard title="Time-Series Overview" subtitle="Views & engagement rate tracked over time. Two Y-axes compare absolute reach (left) with relative engagement (right).">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={unifiedData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(ts) => new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  padding={{ left: 0, right: 0 }}
                />
                <YAxis yAxisId="left" tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={formatLargeNumber} width={45} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={(val) => `${val.toFixed(1)}%`} width={40} />
                <Tooltip content={<CustomTooltip formatter={(val: number, name: string) => name === 'Views' ? formatLargeNumber(val) : val.toFixed(2) + '%'} />} />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="views" name="Views" stroke={COLORS.blue} dot={false} strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="engagement" name="Engagement %" stroke={COLORS.green} dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        {/* CARD 3: Views Trend */}
        <ChartCard title="Views Trend" subtitle="Cumulative view growth over the video's lifetime. A steepening curve signals algorithmic push; flattening means distribution has slowed.">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={unifiedData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(ts) => new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  padding={{ left: 0, right: 0 }}
                />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={formatLargeNumber} width={45} />
                <Tooltip content={<CustomTooltip formatter={(val: number) => formatLargeNumber(val)} />} />
                <Line type="monotone" dataKey="views" name="Views" stroke={COLORS.cyan} dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        {/* CARD 4: Engagement Ratios */}
        <ChartCard title="Engagement Ratios" subtitle="Total engagement rate vs like-to-view rate over time. When the gap widens, comments and shares are driving engagement beyond just likes.">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={unifiedData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(ts) => new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  padding={{ left: 0, right: 0 }}
                />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={(val) => `${val.toFixed(1)}%`} width={40} />
                <Tooltip content={<CustomTooltip formatter={(val: number) => val.toFixed(2) + '%'} />} />
                <Legend />
                <Line type="monotone" dataKey="engagement" name="Total Engagement %" stroke={COLORS.green} dot={false} />
                <Line type="monotone" dataKey="likeRate" name="Likes / View %" stroke={COLORS.red} dot={false} strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        {/* CARD 5: Virality Scatter — 4 quadrants */}
        <ChartCard title="Virality Scatter" subtitle="Each dot is a snapshot. X = total views, Y = share rate. The dashed crosshair splits data at the midpoint — top-right is the viral sweet spot.">
          <QuadrantLabelsTop labels={[
            "Promising spark — low reach, high sharing",
            "Viral & growing — high reach + shares"
          ]} />
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
                <XAxis
                  type="number"
                  dataKey="views"
                  name="Views"
                  scale="log"
                  domain={[chartDomains.views.min, chartDomains.views.max]}
                  allowDataOverflow
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  tickFormatter={formatLargeNumber}
                  label={{ value: 'Views', position: 'insideBottom', fill: '#64748b', fontSize: 9, offset: -5 }}
                />
                <YAxis
                  type="number"
                  dataKey="shareRate"
                  name="Share Rate %"
                  domain={[chartDomains.shareRate.min, chartDomains.shareRate.max]}
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  tickFormatter={(v: number) => v.toFixed(1) + '%'}
                  width={40}
                  label={{ value: 'Share %', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 9, offset: 15 }}
                />
                <ReferenceLine x={chartDomains.views.mid} stroke="#94a3b8" strokeDasharray="5 5" />
                <ReferenceLine y={chartDomains.shareRate.mid} stroke="#94a3b8" strokeDasharray="5 5" />
                <Tooltip content={<CustomTooltip formatter={(val: number, name: string) => name === 'Views' ? formatLargeNumber(val) : val.toFixed(2) + '%'} />} />
                <Scatter name="Virality" data={unifiedData} fill={COLORS.blue} fillOpacity={0.5} shape="circle" />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <QuadrantLabelsBottom labels={[
            "Cold start — low reach, low sharing",
            "Mass reach, low sharing"
          ]} />
        </ChartCard>

        {/* CARD 6: Virality vs Conversation — 4 quadrants */}
        <ChartCard title="Virality vs Conversation" subtitle="Share rate vs comment rate per snapshot. Top-right means the audience both talks about AND shares the video — a strong signal of genuine virality.">
          <QuadrantLabelsTop labels={[
            "Debated — discussed but not shared",
            "Viral conversation — shared AND discussed"
          ]} />
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
                <XAxis
                  type="number"
                  dataKey="shareRate"
                  name="Share Rate %"
                  domain={[chartDomains.shareRate.min, chartDomains.shareRate.max]}
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  tickFormatter={(v: number) => v.toFixed(1) + '%'}
                  label={{ value: 'Share %', position: 'insideBottom', fill: '#64748b', fontSize: 9, offset: -5 }}
                />
                <YAxis
                  type="number"
                  dataKey="commentRate"
                  name="Comment Rate %"
                  domain={[chartDomains.commentRate.min, chartDomains.commentRate.max]}
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  tickFormatter={(v: number) => v.toFixed(2) + '%'}
                  width={45}
                  label={{ value: 'Comment %', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 9, offset: 15 }}
                />
                <ReferenceLine x={chartDomains.shareRate.mid} stroke="#94a3b8" strokeDasharray="5 5" />
                <ReferenceLine y={chartDomains.commentRate.mid} stroke="#94a3b8" strokeDasharray="5 5" />
                <Tooltip content={<CustomTooltip formatter={(val: number) => val.toFixed(3) + '%'} />} />
                <Scatter name="Conversation" data={unifiedData} fill={COLORS.orange} fillOpacity={0.5} />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <QuadrantLabelsBottom labels={[
            "Low engagement — neither shared nor discussed",
            "Silently shared — spread without comment"
          ]} />
        </ChartCard>

        {/* CARD 8: Interaction Mix */}
        <ChartCard title="Interaction Mix" subtitle="Pie chart of total likes, comments, and shares for the latest snapshot. Reveals the dominant action viewers take on this video.">
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={mixData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {mixData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip formatter={(val: number) => val.toLocaleString()} />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        {/* CARD 9: Quality of Engagement — 4 quadrants */}
        <ChartCard title="Quality of Engagement" subtitle="Comments-to-likes ratio vs engagement rate. Top-right means the audience is both engaged AND actively discussing — deeper resonance.">
          <QuadrantLabelsTop labels={[
            "Engaged quietly — high engagement, low comment ratio",
            "Deep resonance — high engagement AND vocal"
          ]} />
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
                <XAxis
                  type="number"
                  dataKey="CLR"
                  name="Comm/Like Ratio"
                  domain={[chartDomains.CLR.min, chartDomains.CLR.max]}
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  tickFormatter={(v: number) => v.toFixed(2)}
                  label={{ value: 'Comments / Likes', position: 'insideBottom', fill: '#64748b', fontSize: 9, offset: -5 }}
                />
                <YAxis
                  type="number"
                  dataKey="engagement"
                  name="Engagement %"
                  domain={[chartDomains.engagement.min, chartDomains.engagement.max]}
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  tickFormatter={(v: number) => v.toFixed(1) + '%'}
                  width={40}
                  label={{ value: 'Engagement %', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 9, offset: 15 }}
                />
                <ReferenceLine x={chartDomains.CLR.mid} stroke="#94a3b8" strokeDasharray="5 5" />
                <ReferenceLine y={chartDomains.engagement.mid} stroke="#94a3b8" strokeDasharray="5 5" />
                <Tooltip content={<CustomTooltip formatter={(val: number, name: string) => name === 'Engagement %' ? val.toFixed(2) + '%' : val.toFixed(3)} />} />
                <Scatter name="Quality" data={unifiedData} fill={COLORS.cyan} fillOpacity={0.5} />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <QuadrantLabelsBottom labels={[
            "Passive — low engagement, few comments",
            "Vocal but sparse — low engagement, comment-heavy"
          ]} />
        </ChartCard>

        {/* CARD 10: Amplification Quality — 4 quadrants */}
        <ChartCard title="Amplification Quality" subtitle="Shares-to-likes ratio vs total views. Top-right dots have both high reach and an audience that amplifies by sharing rather than just liking.">
          <QuadrantLabelsTop labels={[
            "Niche amplifiers — shared heavily, low reach",
            "Power amplification — high reach + sharing"
          ]} />
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
                <XAxis
                  type="number"
                  dataKey="views"
                  scale="log"
                  domain={[chartDomains.views.min, chartDomains.views.max]}
                  allowDataOverflow
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  tickFormatter={formatLargeNumber}
                  label={{ value: 'Views', position: 'insideBottom', fill: '#64748b', fontSize: 9, offset: -5 }}
                />
                <YAxis
                  type="number"
                  dataKey="SLR"
                  name="Share/Like Ratio"
                  domain={[chartDomains.SLR.min, chartDomains.SLR.max]}
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  tickFormatter={(v: number) => v.toFixed(2)}
                  width={40}
                  label={{ value: 'Shares / Likes', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 9, offset: 15 }}
                />
                <ReferenceLine x={chartDomains.views.mid} stroke="#94a3b8" strokeDasharray="5 5" />
                <ReferenceLine y={chartDomains.SLR.mid} stroke="#94a3b8" strokeDasharray="5 5" />
                <Tooltip content={<CustomTooltip formatter={(val: number, name: string) => name === 'Views' ? formatLargeNumber(val) : val.toFixed(3)} />} />
                <Scatter name="Amplification" data={unifiedData} fill={COLORS.orange} fillOpacity={0.5} />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <QuadrantLabelsBottom labels={[
            "Like-heavy, low reach",
            "Mass reach, like-dominated"
          ]} />
        </ChartCard>

        {/* CARD 11: Efficiency Map — 4 quadrants (bubble) */}
        <ChartCard title="Efficiency Map" subtitle="Views vs engagement rate with bubble size = share rate. Top-right bubbles are efficiency winners — high reach with strong engagement.">
          <QuadrantLabelsTop labels={[
            "Niche gem — low reach, high engagement",
            "Efficiency winner — high reach + engagement"
          ]} />
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
                <XAxis
                  type="number"
                  dataKey="views"
                  scale="log"
                  domain={[chartDomains.views.min, chartDomains.views.max]}
                  allowDataOverflow
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  tickFormatter={formatLargeNumber}
                  label={{ value: 'Views', position: 'insideBottom', fill: '#64748b', fontSize: 9, offset: -5 }}
                />
                <YAxis
                  type="number"
                  dataKey="engagement"
                  name="Engagement %"
                  domain={[chartDomains.engagement.min, chartDomains.engagement.max]}
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  tickFormatter={(v: number) => v.toFixed(1) + '%'}
                  width={40}
                  label={{ value: 'Engagement %', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 9, offset: 15 }}
                />
                <ZAxis type="number" dataKey="shareRate" range={[50, 400]} name="Share Rate %" />
                <ReferenceLine x={chartDomains.views.mid} stroke="#94a3b8" strokeDasharray="5 5" />
                <ReferenceLine y={chartDomains.engagement.mid} stroke="#94a3b8" strokeDasharray="5 5" />
                <Tooltip content={<CustomTooltip formatter={(val: number, name: string) => name === 'Views' ? formatLargeNumber(val) : val.toFixed(2) + '%'} />} />
                <Scatter name="Efficiency" data={unifiedData} fill={COLORS.blue} fillOpacity={0.5} stroke={COLORS.blue} />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <QuadrantLabelsBottom labels={[
            "Underperforming — low reach & engagement",
            "Mass reach, low engagement"
          ]} />
        </ChartCard>

        {/* CARD 12: Recent Alerts */}
        <div className="col-span-1 lg:col-span-2">
          <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/30 text-center text-slate-500 text-sm">
            To see recent alerts, please enable Anomaly Detection (Coming Soon)
          </div>
        </div>

      </div>
    </div>
  );
};

export default ReportTab;
